<template>
  <div class="companies">
    <SectionHeader
      :projectSelect="true"
      @selectProjectUuids="
        projectUuids = $event
        getContainers()
      "
    >
      <template v-slot:breadcrumbs>
        <BreadCrumbs :items="[{ text: $t('meta.containers') }]" />
      </template>

      <template v-slot:meta>
        <div class="button-group">
          <button
            @click="downloadContainersReport"
            :disabled="downloading"
            class="button button-green button-round button-small button--csv material-icons"
          >
            file_download
          </button>
          <Link
            :to="'/company/containers/import'"
            class="button button-green button-round button-small button--csv material-icons"
            >file_upload</Link
          >
          <Link :to="'/company/containers/new'" class="button button-green button-round button-small">{{
            $t('containers.addContainer')
          }}</Link>
        </div>
      </template>
    </SectionHeader>

    <div class="body-content cf">
      <CompanyNav />
      <div class="body">
        <SearchField
          v-if="!showLoadingSpinner"
          :label="$t('meta.searchForLabel', { entity: $t('meta.container') })"
          :name="'container-search-input'"
          :placeholder="$t('containers.searchPlaceholder')"
          v-model="searchPhrase"
          @input="searchHandler"
        />
        <LoadingSpinner v-if="loading && showLoadingSpinner" />
        <div v-else-if="containers && containers.length == 0" class="alert alert-no-entry">
          {{ $t('containers.noContainers') }}
        </div>
        <template v-else>
          <LinkList>
            <LinkListItem
              v-for="container in containers"
              :key="container.uuid"
              :primaryLink="`/company/containers/${container.uuid}`"
              :class="[container.status]"
            >
              <template v-slot:main-content>
                <span>{{ container.name }}</span>
                <span>{{ container.preformatted }}</span>
                <span>{{ container.project.name }}</span>
              </template>
              <template #action-content>
                <Link :to="`/company/containers/${container.uuid}`" class="link-list-item__action"
                  ><span class="material-icons">edit</span></Link
                >
                <button @click="toggleVisibility(container)" class="link-list-item__action">
                  <span class="material-icons">{{
                    container.status === 'enabled' ? 'visibility' : 'visibility_off'
                  }}</span>
                </button>
                <button @click="deleteContainer(container)" class="link-list-item__action">
                  <span class="material-icons">delete</span>
                </button>
              </template>
            </LinkListItem>
          </LinkList>

          <pagination
            v-if="pagination"
            :records="pagination.totalObjects"
            v-model="pagination.page"
            :per-page="pagination.perPage"
            @paginate="getContainers"
            :options="{ chunk: 5, texts: { count: $t('pagination.text') } }"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import Pagination from 'vue-pagination-2'

import BreadCrumbs from '@/components/BreadCrumbs/index.vue'
import SearchField from '@/components/Form/Search/SearchField'
import LinkListItem from '@/components/LinkList/LinkListItem.vue'
import LinkList from '@/components/LinkList/index.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import CompanyNav from '@/components/company_nav'

import containersApi from '@/api/container'
import exportsApi from '@/api/exports'
import { SEARCH_INPUT_DELAY } from '@/utils/searchInputDelay'

export default {
  name: 'company_containers',
  inject: ['companyUser', 'showPopup'],
  props: {
    searchInputDelay: {
      type: Number,
      default: SEARCH_INPUT_DELAY,
    },
  },
  data: () => ({
    containers: [],
    currentPage: 1,
    downloading: false,
    loading: true,
    pagination: { page: 1, totalObjects: 0 },
    projectUuids: [],
    searchPhrase: '',
    showLoadingSpinner: true,
  }),
  components: {
    BreadCrumbs,
    CompanyNav,
    LinkList,
    LinkListItem,
    LoadingSpinner,
    Pagination,
    SearchField,
    SectionHeader,
  },
  computed: {
    searchHandler() {
      return debounce(
        () => {
          // The resulting records of the search cause a new pagination
          // and we start with page 1 for showing the results.
          this.getContainers(1, false)
        },
        this.searchInputDelay, // Limit search API calls to once per 400ms while typing
        { trailing: true },
      )
    },
  },
  methods: {
    goToContainer(containerUuid) {
      this.$router.push(`/company/containers/${containerUuid}`)
    },
    async getContainers(page = 1, showLoadingSpinner = true) {
      this.currentPage = page || this.pagination.page
      this.loading = true
      this.showLoadingSpinner = showLoadingSpinner

      const params = {
        company_uuid: this.companyUser.company_uuid,
        project_uuids: this.projectUuids,
        with_disabled: true,
        page: this.currentPage,
      }

      if (this.searchPhrase.length > 0) {
        params['search_phrase'] = this.searchPhrase
      }

      const result = await containersApi.getAll(params)

      this.containers = result.containers
      this.pagination.page = this.currentPage
      this.pagination.totalObjects = result.pagination?.total_objects
      this.loading = false
      this.showLoadingSpinner = false
    },
    async toggleVisibility(container) {
      const status = container.status == 'disabled' ? 'enabled' : 'disabled'
      await this.updateContainerStatus(container.uuid, status)
    },
    async deleteContainer(container) {
      const confirmAlert = await this.showPopup({ content: this.$t('containers.deleteConfirmation'), type: 'confirm' })
      if (!confirmAlert) return null

      await this.updateContainerStatus(container.uuid, 'deleted')
    },
    async updateContainerStatus(containerUuid, newStatus) {
      const result = await containersApi.update(containerUuid, {
        status: newStatus,
      })
      if (result) {
        await this.getContainers(this.currentPage)
      } else {
        this.$notify({
          group: 'alert',
          type: 'error',
          title: this.$t('meta.noSuccess'),
        })
      }
    },
    async downloadContainersReport() {
      this.downloading = true
      await exportsApi.create({
        type: 'containers_xlsx',
        company_uuid: this.companyUser.company_uuid,
        project_uuids: this.projectUuids,
      })
      this.downloading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.button-group {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: 0.75rem;

  & .button {
    margin: 0 !important;
  }
}
.button--csv {
  font-size: 1.25rem;
  line-height: 1.5;
  padding: 3px 1rem;
}
</style>
